import { Box, styled } from "@mui/material"
import { ConventionLocationStyle } from "app-constants/constants"
import { createPath, createURL_SSR, ROUTE } from "app-constants/routing"
import MainLayout from "components/main-layout"
import SessionDetails from "components/sessions/SessionDetails"
import buildServerSidePage from "lib/build-server-side-page"
import buildServerSideProps from "lib/build-server-side-props"
import applyCache from "lib/mw/apply-cache"
import applyConvention from "lib/mw/apply-convention"
import applyCurrentContext from "lib/mw/apply-current-context"
import applyNextSeo from "lib/mw/apply-next-seo"
import applyRequiredProps from "lib/mw/apply-required-props"
import applyTitle from "lib/mw/apply-title"
import ConventionSession from "models/convention-api/v1/ConventionSession"
import React from "react"
import { pxToRem } from "utils/helpers"

export const getServerSideProps = buildServerSideProps(
  applyCache(),
  applyCurrentContext(),
  applyConvention(),
  [
    (context) => {
      if (context.props.convention?.location_style == ConventionLocationStyle.INPERSON) {
        context.props.redirect = createPath({
          path: ROUTE.CONVENTION,
          params: { conventionId: context.params.convention_id as string },
        })
        context.abort()
        return
      }
    },
  ],
  [
    ConventionSession.applyOne("session", ({ params }) => ({
      filter: {
        id: params.session_id as string,
      },
      includes: [
        "capacity",
        "attachments",
        "assignments",
        "assignments.admin_user",
        "taggings",
        "taggings.tag",
        "convention_exhibitor",
      ],
    })),
  ],
  applyRequiredProps("session"),
  applyTitle(({ props }) => `${props.session.name}`),
  applyNextSeo((context) => ({
    openGraph: {
      title: context.props.session.name,
      url: createURL_SSR(context, {
        path: ROUTE.CONVENTION_SESSION,
        params: {
          conventionId: context.query.convention_id as string,
          sessionId: context.props.session.id,
        },
      }),
      type: "website",
      images: [
        {
          url: context.props.session.promotional_image_thumb_url,
          alt: context.props.session.name,
        },
      ],
    },
  })),
)

interface ConventionSessionPageProps {
  session: ConventionSession
}

export default buildServerSidePage(function ConventionSessionPage(
  props: ConventionSessionPageProps,
) {
  const { session } = props

  return (
    <Box sx={{ overflow: "hidden" }}>
      <MainLayout layout="micro">
        <_WithTopBackground>
          <SessionDetails session={session} />
        </_WithTopBackground>
      </MainLayout>
    </Box>
  )
})

const _WithTopBackground = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  justifyContent: 'center',
  width: "100%",
  height: "100%",
  overflowX: 'hidden',
  "&::before": {
    content: "''",
    position: "absolute",
    left: 0,
    right: 0,
    height: pxToRem(176),
    width: "100vw",
    backgroundColor: theme.palette.primary.lightest,
  },
})) as typeof Box
